import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import api from '@/utils/api';
import { toast } from 'sonner';
import {
  LogOut, Users, Shield, CheckCircle, XCircle, Plus, Trash2,
  ClipboardList, BarChart3, Search, UserPlus
} from 'lucide-react';
import MessagesNavButton from '@/components/MessagesNavButton';
import XVAssistWidget from '@/components/XVAssistWidget';
import CoachOverviewSection from '@/components/CoachOverviewSection';
import ModuleStripCard from '@/components/ModuleStripCard';

export default function CoachDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [teams, setTeams] = useState([]);
  const [verifications, setVerifications] = useState([]);
  const [profileApprovals, setProfileApprovals] = useState([]);
  const [changesFor, setChangesFor] = useState(null);
  const [changesNotes, setChangesNotes] = useState('');
  const [previewFor, setPreviewFor] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [validateTesting, setValidateTesting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showCreateTeam, setShowCreateTeam] = useState(false);
  const [newTeam, setNewTeam] = useState({ name: '', school_club: '' });
  const [searchResults, setSearchResults] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [addingToTeam, setAddingToTeam] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [dashRes, teamsRes, verRes, appRes] = await Promise.all([
        api.get('/coach/dashboard'),
        api.get('/coach/teams'),
        api.get('/coach/verifications'),
        api.get('/coach/profile-approvals')
      ]);
      setStats(dashRes.data);
      setTeams(teamsRes.data);
      setVerifications(verRes.data);
      setProfileApprovals(appRes.data);
    } catch (error) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreateTeam = async (e) => {
    e.preventDefault();
    try {
      await api.post('/coach/teams', newTeam);
      toast.success('Team created');
      setNewTeam({ name: '', school_club: '' });
      setShowCreateTeam(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create team');
    }
  };

  const handleEndorse = async (id) => {
    try {
      await api.post(`/coach/verifications/${id}/endorse`);
      toast.success('Verification endorsed');
      fetchData();
    } catch (error) {
      toast.error('Failed to endorse');
    }
  };

  const handleProfileDecision = async (requestId, action, reason) => {
    try {
      const res = await api.post(`/coach/profile-approvals/${requestId}/decision`, {
        action, reason: reason || null,
        validate_testing: action === 'accepted' ? validateTesting : false,
      });
      toast.success(action === 'accepted'
        ? `${res.data.message || 'Profile approved'}${res.data.testing_validated ? ' — testing data marked coach-validated' : ''}`
        : 'Changes requested — player notified');
      setChangesFor(null);
      setChangesNotes('');
      setPreviewFor(null);
      setPreviewData(null);
      setValidateTesting(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit decision');
    }
  };

  const openPreview = async (row) => {
    setPreviewFor(row);
    setPreviewData(null);
    try {
      const res = await api.get(`/coach/profile-approvals/${row.request_id}/profile`);
      setPreviewData(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load profile preview');
      setPreviewFor(null);
    }
  };

  const handleSearchPlayers = async () => {
    if (!searchQuery.trim()) return;
    try {
      const res = await api.get(`/coach/search-players?school_club=${encodeURIComponent(searchQuery)}`);
      setSearchResults(res.data);
    } catch (error) {
      toast.error('Search failed');
    }
  };

  const handleAddPlayer = async (teamId, playerId) => {
    try {
      await api.post(`/coach/teams/${teamId}/add-player?player_id=${playerId}`);
      toast.success('Player added to team');
      fetchData();
      setAddingToTeam(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add player');
    }
  };

  const handleRemovePlayer = async (teamId, playerId) => {
    try {
      await api.delete(`/coach/teams/${teamId}/remove-player/${playerId}`);
      toast.success('Player removed');
      fetchData();
    } catch (error) {
      toast.error('Failed to remove player');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="text-primary-neon text-xl">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-white" data-testid="coach-dashboard">
      <nav className="border-b border-zinc-800 bg-zinc-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <img src="/logo.png" alt="FutureXV" className="h-10" />
              <span className="text-zinc-600">|</span>
              <span className="font-heading font-bold">Coach Dashboard</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-zinc-400 text-sm hidden sm:block">{user?.email}</span>
              <Button variant="outline" size="sm" onClick={() => navigate('/playbook')} className="border-zinc-700 text-zinc-300 mr-1" data-testid="playbook-nav-button">Playbook</Button>
              <MessagesNavButton />
              <Button onClick={() => { logout(); navigate('/'); }} variant="ghost" className="text-zinc-400 hover:text-white" data-testid="coach-logout-button">
                <LogOut className="w-4 h-4 mr-2" />Logout
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-heading font-bold mb-2" data-testid="coach-welcome">Welcome, {user?.full_name}</h1>
          <p className="text-zinc-400 font-body">Manage your teams and verify player profiles</p>
        </div>

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="coach-stats">
            <Card className="bg-zinc-900 border-zinc-800 p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary-neon/10 rounded-lg flex items-center justify-center"><Users className="w-5 h-5 text-primary-neon" /></div>
                <div><p className="text-zinc-400 text-xs">Teams</p><p className="text-xl font-heading font-bold">{stats.total_teams}</p></div>
              </div>
            </Card>
            <Card className="bg-zinc-900 border-zinc-800 p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-500/10 rounded-lg flex items-center justify-center"><Users className="w-5 h-5 text-blue-400" /></div>
                <div><p className="text-zinc-400 text-xs">Players</p><p className="text-xl font-heading font-bold">{stats.total_players}</p></div>
              </div>
            </Card>
            <Card className="bg-zinc-900 border-zinc-800 p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-yellow-500/10 rounded-lg flex items-center justify-center"><Shield className="w-5 h-5 text-yellow-400" /></div>
                <div><p className="text-zinc-400 text-xs">Pending</p><p className="text-xl font-heading font-bold">{stats.pending_verifications}</p></div>
              </div>
            </Card>
            <Card className="bg-zinc-900 border-zinc-800 p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-500/10 rounded-lg flex items-center justify-center"><CheckCircle className="w-5 h-5 text-green-400" /></div>
                <div><p className="text-zinc-400 text-xs">Total Verifications</p><p className="text-xl font-heading font-bold">{stats.total_verifications}</p></div>
              </div>
            </Card>
          </div>
        )}

        <ModuleStripCard role="coach" />

        {profileApprovals.length > 0 && (
          <Card className="bg-zinc-900 border border-amber-500/30 p-6 mb-8" data-testid="pending-approvals-card">
            <div className="flex items-center gap-3 mb-4">
              <Shield className="w-5 h-5 text-amber-400" />
              <h2 className="text-xl font-heading font-bold">Pending Player Approvals</h2>
              <Badge className="bg-amber-500/10 text-amber-400 border border-amber-500/30" data-testid="pending-approvals-count">{profileApprovals.length}</Badge>
            </div>
            <div className="space-y-3">
              {profileApprovals.map((a) => (
                <div key={a.request_id} className="bg-zinc-950 border border-zinc-800 rounded-lg p-4 flex flex-col md:flex-row md:items-center gap-4" data-testid={`approval-row-${a.request_id}`}>
                  <div className="flex-1">
                    <p className="font-heading font-bold">{a.player_name} {a.is_u18 && <Badge className="ml-2 bg-blue-500/10 text-blue-400 border border-blue-500/20 text-[10px]">U18</Badge>}</p>
                    <p className="text-zinc-400 text-sm mt-0.5">
                      {a.position || '—'} · {a.school_club || 'No school/club'} · {a.province || '—'}
                    </p>
                    <p className="text-zinc-600 text-xs mt-0.5">{a.player_email}{a.dob ? ` · DOB ${a.dob}` : ''}</p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Button onClick={() => openPreview(a)} className="bg-green-600 hover:bg-green-500 text-white" size="sm" data-testid={`approve-profile-${a.request_id}`}>
                      <CheckCircle className="w-4 h-4 mr-1.5" />Review &amp; Approve
                    </Button>
                    <Button onClick={() => { setChangesFor(a); setChangesNotes(''); }} variant="outline" className="border-amber-500/40 text-amber-400 hover:bg-amber-500/10" size="sm" data-testid={`request-changes-${a.request_id}`}>
                      <XCircle className="w-4 h-4 mr-1.5" />Request Changes
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            {previewFor && (
              <Dialog open onOpenChange={(o) => { if (!o) { setPreviewFor(null); setPreviewData(null); } }}>
                <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="profile-preview-dialog">
                  <DialogHeader><DialogTitle className="font-heading">Review Profile — {previewFor.player_name}</DialogTitle></DialogHeader>
                  {!previewData ? (
                    <p className="text-zinc-500 py-6 text-center">Loading profile…</p>
                  ) : (() => {
                    const p = previewData.profile || {};
                    const pl = previewData.player || {};
                    const tests = [
                      ['Body Mass', p.body_mass_kg, 'kg'], ['Height', p.height_cm, 'cm'],
                      ['Body Fat (derived)', p.body_fat_pct, '%'], ['Muscle Mass (derived)', p.muscle_mass_kg, 'kg'],
                      ['10m Sprint', p.sprint_10m_sec, 's'], ['40m Sprint', p.sprint_40m_sec, 's'],
                      ['Illinois Agility', p.illinois_agility_sec, 's'], ['Vertical Jump', p.vertical_jump_cm, 'cm'],
                      ['Broad Jump', p.broad_jump_cm, 'cm'], ['Bench 1RM', p.bench_press_1rm_kg, 'kg'],
                      ['Bench 1RM (calc)', p.bench_press_1rm_calculated_kg, 'kg'], ['Squat 1RM', p.squat_1rm_kg, 'kg'],
                      ['Pull-Ups', p.pull_ups_reps, 'reps'], ['Push-Ups (1min)', p.push_ups_1min_reps, 'reps'],
                      ['Sit-Ups (2min)', p.sit_ups_2min_reps, 'reps'],
                      ['Bleep Test', p.bleep_test_level != null && p.bleep_test_shuttle != null ? `${p.bleep_test_level}.${p.bleep_test_shuttle}` : null, ''],
                      ['VO₂max (derived)', p.vo2max_estimated, 'ml/kg/min'], ['3km Time', p.run_3km_time, ''],
                    ].filter(([, v]) => v != null && v !== '');
                    return (
                      <div className="space-y-4">
                        <div className="flex items-center gap-4">
                          {p.profile_photo
                            ? <img src={p.profile_photo} alt={pl.full_name} className="w-16 h-16 rounded-full object-cover border-2 border-primary-neon" data-testid="preview-photo" />
                            : <div className="w-16 h-16 rounded-full bg-zinc-800 flex items-center justify-center text-zinc-600 font-heading font-bold text-xl">{(pl.full_name || '?')[0]}</div>}
                          <div>
                            <p className="font-heading font-bold text-lg">{pl.full_name} {pl.is_u18 && <Badge className="ml-1 bg-blue-500/10 text-blue-400 border border-blue-500/20 text-[10px]">U18</Badge>}</p>
                            <p className="text-zinc-400 text-sm">{pl.email}{pl.dob ? ` · DOB ${pl.dob}` : ''}{pl.gender ? ` · ${pl.gender}` : ''}</p>
                            {pl.is_u18 && (pl.guardian_name || pl.guardian_email) && (
                              <p className="text-zinc-500 text-xs mt-0.5">Guardian: {pl.guardian_name || '—'} ({pl.guardian_email || '—'})</p>
                            )}
                          </div>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm" data-testid="preview-rugby-info">
                          {[['Position', p.preferred_position], ['Other Positions', (p.other_positions || []).join(', ') || null],
                            ['School / Club', p.school_club], ['Province', p.province], ['Home Language', p.home_language],
                            ['Experience', p.playing_experience_years != null ? `${p.playing_experience_years} yrs` : null],
                            ['Season Phase', p.season_phase], ['Training Days/Week', p.training_days_per_week],
                            ['XV Assist Goal', p.xv_assist_goal]]
                            .filter(([, v]) => v != null && v !== '')
                            .map(([l, v]) => (
                              <div key={l} className="bg-zinc-950 border border-zinc-800 rounded p-2">
                                <p className="text-zinc-500 text-[11px]">{l}</p>
                                <p className="text-zinc-200 font-semibold text-xs mt-0.5">{v}</p>
                              </div>
                            ))}
                        </div>
                        {p.current_injury_notes && (
                          <div className="bg-red-500/10 border border-red-500/30 rounded p-3 text-sm text-red-300" data-testid="preview-injury-notes">
                            <span className="font-bold">Injury / medical note:</span> {p.current_injury_notes}
                          </div>
                        )}
                        <div>
                          <p className="font-heading font-bold text-sm mb-2">Physical Testing (BokSmart) <span className="text-zinc-500 font-normal text-xs">— {p.data_source === 'self_reported' ? 'self-reported' : 'coach-validated'}</span></p>
                          {tests.length === 0 ? (
                            <p className="text-zinc-500 text-sm">No physical testing data submitted yet.</p>
                          ) : (
                            <div className="grid grid-cols-3 md:grid-cols-4 gap-2" data-testid="preview-testing-grid">
                              {tests.map(([l, v, u]) => (
                                <div key={l} className="bg-zinc-950 border border-zinc-800 rounded p-2 text-center">
                                  <p className="text-primary-neon font-heading font-bold">{v}{u && <span className="text-[10px] text-zinc-500 ml-0.5">{u}</span>}</p>
                                  <p className="text-zinc-500 text-[10px] mt-0.5">{l}</p>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        <label className="flex items-start gap-3 cursor-pointer bg-zinc-950 border border-zinc-800 rounded p-3" data-testid="validate-testing-label">
                          <input type="checkbox" checked={validateTesting} onChange={(e) => setValidateTesting(e.target.checked)}
                            className="mt-0.5 w-4 h-4 rounded border-zinc-600 bg-zinc-900 text-primary-neon focus:ring-primary-neon" data-testid="validate-testing-checkbox" />
                          <span className="text-zinc-300 text-sm">I have reviewed this player's physical testing data and validate it as accurate. <span className="text-zinc-500">(Removes the "self-reported" flag — data becomes coach-validated on the PDS record.)</span></span>
                        </label>
                        <div className="flex gap-3 pt-1">
                          <Button onClick={() => handleProfileDecision(previewFor.request_id, 'accepted')} className="bg-green-600 hover:bg-green-500 text-white" data-testid="confirm-approve-button">
                            <CheckCircle className="w-4 h-4 mr-1.5" />Approve Profile
                          </Button>
                          <Button onClick={() => { setChangesFor(previewFor); setChangesNotes(''); setPreviewFor(null); setPreviewData(null); }} variant="outline" className="border-amber-500/40 text-amber-400 hover:bg-amber-500/10" data-testid="preview-request-changes-button">
                            <XCircle className="w-4 h-4 mr-1.5" />Request Changes
                          </Button>
                          <Button variant="outline" onClick={() => { setPreviewFor(null); setPreviewData(null); }} className="border-zinc-700 text-zinc-400">Cancel</Button>
                        </div>
                      </div>
                    );
                  })()}
                </DialogContent>
              </Dialog>
            )}
            {changesFor && (
              <Dialog open onOpenChange={(o) => { if (!o) setChangesFor(null); }}>
                <DialogContent className="bg-zinc-900 border-zinc-800 text-white">
                  <DialogHeader><DialogTitle className="font-heading">Request Changes — {changesFor.player_name}</DialogTitle></DialogHeader>
                  <p className="text-zinc-400 text-sm">The profile will be returned to draft with your notes. The player is notified by email and can correct and resubmit.</p>
                  <Label className="text-zinc-300 mt-2">Notes for the player</Label>
                  <Input value={changesNotes} onChange={(e) => setChangesNotes(e.target.value)} placeholder="e.g. Please confirm your 40m sprint time and add your school" className="bg-zinc-950 border-zinc-800 text-white" data-testid="changes-notes-input" />
                  <div className="flex gap-3 mt-2">
                    <Button onClick={() => handleProfileDecision(changesFor.request_id, 'declined', changesNotes)} className="bg-amber-600 hover:bg-amber-500 text-white" data-testid="confirm-request-changes-button">Send to Player</Button>
                    <Button variant="outline" onClick={() => setChangesFor(null)} className="border-zinc-700 text-zinc-400">Cancel</Button>
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </Card>
        )}

        <CoachOverviewSection />

        <Tabs defaultValue="teams" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-8 bg-zinc-900 max-w-md" data-testid="coach-tabs">
            <TabsTrigger value="teams" className="data-[state=active]:bg-primary-neon data-[state=active]:text-black" data-testid="teams-tab">
              <ClipboardList className="w-4 h-4 mr-2" />Teams
            </TabsTrigger>
            <TabsTrigger value="verifications" className="data-[state=active]:bg-primary-neon data-[state=active]:text-black" data-testid="verifications-tab">
              <Shield className="w-4 h-4 mr-2" />Verifications
            </TabsTrigger>
          </TabsList>

          <TabsContent value="teams" data-testid="teams-content">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-heading font-bold">Your Teams</h2>
              <Button onClick={() => setShowCreateTeam(true)} className="btn-primary" data-testid="create-team-button">
                <Plus className="w-4 h-4 mr-2" />Create Team
              </Button>
            </div>

            {showCreateTeam && (
              <Card className="bg-zinc-900 border-zinc-800 p-6 mb-6" data-testid="create-team-form">
                <form onSubmit={handleCreateTeam} className="flex flex-col sm:flex-row gap-4">
                  <Input value={newTeam.name} onChange={(e) => setNewTeam({...newTeam, name: e.target.value})} placeholder="Team name (e.g. U18 First XV)" className="bg-zinc-950 border-zinc-800 text-white flex-1" required data-testid="team-name-input" />
                  <Input value={newTeam.school_club} onChange={(e) => setNewTeam({...newTeam, school_club: e.target.value})} placeholder="School/Club name" className="bg-zinc-950 border-zinc-800 text-white flex-1" required data-testid="team-school-input" />
                  <Button type="submit" className="btn-primary" data-testid="submit-team-button">Create</Button>
                  <Button type="button" variant="outline" onClick={() => setShowCreateTeam(false)} className="border-zinc-700 text-zinc-400">Cancel</Button>
                </form>
              </Card>
            )}

            {teams.length === 0 ? (
              <Card className="bg-zinc-900 border-zinc-800 p-12 text-center">
                <ClipboardList className="w-12 h-12 mx-auto mb-4 text-zinc-700" />
                <p className="text-zinc-500">No teams yet. Create your first team to start managing players.</p>
              </Card>
            ) : (
              <div className="space-y-6">
                {teams.map((team) => (
                  <Card key={team.id} className="bg-zinc-900 border-zinc-800 p-6" data-testid={`team-${team.id}`}>
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-xl font-heading font-bold">{team.name}</h3>
                        <p className="text-zinc-400 text-sm">{team.school_club} &middot; {team.players?.length || 0} players</p>
                      </div>
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button size="sm" className="btn-primary" onClick={() => setAddingToTeam(team.id)} data-testid={`add-player-to-team-${team.id}`}>
                            <UserPlus className="w-4 h-4 mr-1" />Add Player
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="bg-zinc-900 border-zinc-800 text-white">
                          <DialogHeader><DialogTitle className="font-heading">Add Player to {team.name}</DialogTitle></DialogHeader>
                          <div className="space-y-4">
                            <div className="flex gap-2">
                              <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search by school/club name..." className="bg-zinc-950 border-zinc-800 text-white" data-testid="search-player-input" />
                              <Button onClick={handleSearchPlayers} className="btn-primary" data-testid="search-player-button"><Search className="w-4 h-4" /></Button>
                            </div>
                            <div className="max-h-60 overflow-y-auto space-y-2">
                              {searchResults.map((p) => (
                                <div key={p.id} className="flex justify-between items-center bg-zinc-950 p-3 rounded-lg">
                                  <div>
                                    <p className="font-semibold text-sm">{p.name}</p>
                                    <p className="text-xs text-zinc-400">{p.position} &middot; Age {p.age} &middot; {p.school_club}</p>
                                  </div>
                                  <Button size="sm" onClick={() => handleAddPlayer(team.id, p.id)} className="bg-primary-neon text-black hover:bg-primary-neon/90 h-8" data-testid={`add-btn-${p.id}`}>Add</Button>
                                </div>
                              ))}
                              {searchResults.length === 0 && <p className="text-zinc-500 text-sm text-center py-4">Search for players to add</p>}
                            </div>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </div>

                    {team.players && team.players.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="border-b border-zinc-800">
                            <tr>
                              <th className="px-3 py-2 text-left text-xs text-zinc-400 uppercase">Name</th>
                              <th className="px-3 py-2 text-left text-xs text-zinc-400 uppercase">Position</th>
                              <th className="px-3 py-2 text-left text-xs text-zinc-400 uppercase">Age</th>
                              <th className="px-3 py-2 text-left text-xs text-zinc-400 uppercase">Verified</th>
                              <th className="px-3 py-2 text-right text-xs text-zinc-400 uppercase">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {team.players.map((p) => (
                              <tr key={p.profile?.id || Math.random()} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                                <td className="px-3 py-3 text-sm">{p.user?.full_name || 'Unknown'}</td>
                                <td className="px-3 py-3 text-sm text-zinc-400">{p.profile?.position}</td>
                                <td className="px-3 py-3 text-sm text-zinc-400">{p.profile?.age}</td>
                                <td className="px-3 py-3">
                                  {p.profile?.verified ? (
                                    <Badge className="bg-green-500/10 text-green-400 border border-green-500/20">Verified</Badge>
                                  ) : (
                                    <Badge className="bg-zinc-500/10 text-zinc-400 border border-zinc-500/20">Unverified</Badge>
                                  )}
                                </td>
                                <td className="px-3 py-3 text-right">
                                  <Button size="sm" variant="ghost" onClick={() => handleRemovePlayer(team.id, p.profile?.id)} className="text-red-400 hover:text-red-300 h-8" data-testid={`remove-player-${p.profile?.id}`}>
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-zinc-500 text-sm text-center py-4">No players in this team yet</p>
                    )}
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="verifications" data-testid="verifications-content">
            <h2 className="text-2xl font-heading font-bold mb-6">Verification Requests</h2>
            {verifications.length === 0 ? (
              <Card className="bg-zinc-900 border-zinc-800 p-12 text-center">
                <Shield className="w-12 h-12 mx-auto mb-4 text-zinc-700" />
                <p className="text-zinc-500">No verification requests linked to your email</p>
              </Card>
            ) : (
              <div className="space-y-4">
                {verifications.map((v) => (
                  <Card key={v.id} className="bg-zinc-900 border-zinc-800 p-5" data-testid={`verification-${v.id}`}>
                    <div className="flex justify-between items-center">
                      <div>
                        <h3 className="font-heading font-bold text-lg">{v.player_name}</h3>
                        <p className="text-zinc-400 text-sm">{v.school_club}</p>
                        <Badge className={
                          v.status === 'pending' ? 'mt-2 bg-yellow-500/10 text-yellow-400 border border-yellow-500/20' :
                          v.status === 'endorsed' ? 'mt-2 bg-green-500/10 text-green-400 border border-green-500/20' :
                          'mt-2 bg-zinc-500/10 text-zinc-400 border border-zinc-500/20'
                        }>{v.status}</Badge>
                      </div>
                      {v.status === 'pending' && (
                        <Button onClick={() => handleEndorse(v.id)} className="bg-green-600 hover:bg-green-700 text-white" data-testid={`endorse-btn-${v.id}`}>
                          <CheckCircle className="w-4 h-4 mr-2" />Endorse
                        </Button>
                      )}
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
      <XVAssistWidget />
    </div>
  );
}
